import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { Tooltip } from 'primereact/tooltip';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import axiosInstance from '../config/axios.config';
import { fetchPageData } from '../services/pageDataService';
import { getProcessingStatusConfig } from '../services/leadProcessingService';
import { requestUnmask, approveUnmask, rejectUnmask } from '../config/sharedLeadApi';
import { useMasking } from '../layout/context/maskingcontext';

const SharedLeads = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const [loading, setLoading] = useState(true); // Start with loading true
    const [initialLoad, setInitialLoad] = useState(true); // Track initial load
    const [globalFilter, setGlobalFilter] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);

    // Masking context hook
    const { maskEmail, maskPhone, maskPAN, isMasked } = useMasking();
    
    // States for different types of shares
    const [sharedWithMe, setSharedWithMe] = useState([]);
    const [pendingShares, setPendingShares] = useState([]);
    const [sharedByMe, setSharedByMe] = useState([]);

    // Edit access dialog state
    const [editAccessDialogVisible, setEditAccessDialogVisible] = useState(false);
    const [selectedShareForEdit, setSelectedShareForEdit] = useState(null);
    const [newAccessLevel, setNewAccessLevel] = useState(null);
    const [includeReports, setIncludeReports] = useState(false);
    const [updatingAccess, setUpdatingAccess] = useState(false);

    // Unmask request dialog state
    const [unmaskDialogVisible, setUnmaskDialogVisible] = useState(false);
    const [selectedShareForUnmask, setSelectedShareForUnmask] = useState(null);
    const [unmaskMessage, setUnmaskMessage] = useState('');
    const [submittingUnmask, setSubmittingUnmask] = useState(false);

    // Completion modal state
    const [completionModalVisible, setCompletionModalVisible] = useState(false);
    const [selectedShareForCompletion, setSelectedShareForCompletion] = useState(null);

    // Access level options
    const accessLevelOptions = [
        { label: 'View Only', value: 'view', icon: 'pi pi-eye', severity: 'info' },
        { label: 'Can Edit', value: 'edit', icon: 'pi pi-pencil', severity: 'warning' },
        { label: 'Full Access', value: 'full', icon: 'pi pi-star', severity: 'success' }
    ];

    // Dialog states
    // These states are no longer needed since we navigate to a new page
    // const [selectedLead, setSelectedLead] = useState(null);
    // const [detailsVisible, setDetailsVisible] = useState(false);
    // const [selectedShare, setSelectedShare] = useState(null);
    // const [reportsVisible, setReportsVisible] = useState(false);
    // const [sharedReports, setSharedReports] = useState([]);
    // const [loadingReports, setLoadingReports] = useState(false);

    useEffect(() => {
        loadSharedLeads();
    }, []);

    const loadSharedLeads = async () => {
        setLoading(true);
        try {
            // One request for all three lists (#162). Previously three, fired
            // through Promise.all — which also meant ONE failing list rejected
            // the batch and the page rendered nothing. The aggregate reports per
            // section, so an outage in "shared by me" no longer blanks the two
            // tabs that loaded fine.
            const page = await fetchPageData('shared-leads');

            const withMeRes = page.section('shared_with_me');
            const pendingRes = page.section('pending');
            const byMeRes = page.section('shared_by_me');

            if (withMeRes.success) {
                setSharedWithMe(withMeRes.data);
            }
            if (pendingRes.success) {
                setPendingShares(pendingRes.data);
            }
            if (byMeRes.success) {
                setSharedByMe(byMeRes.data);
            }

            const failed = page.failed();
            if (failed.length > 0) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Partly loaded',
                    detail: `Could not load: ${failed.join(', ')}`,
                    life: 4000
                });
            }
        } catch (error) {
            console.error('Error loading shared leads:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load shared leads',
                life: 3000
            });
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    };

    const handleAcceptShare = async (shareId) => {
        try {
            console.log('Accepting share:', shareId);
            const response = await axiosInstance.post(`/leads/shared/${shareId}/accept`);
            console.log('Accept response:', response);
            
            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Share request accepted',
                    life: 3000
                });
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error accepting share:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to accept share request',
                life: 3000
            });
        }
    };

    const handleRejectShare = async (shareId) => {
        try {
            console.log('Rejecting share:', shareId);
            const response = await axiosInstance.post(`/leads/shared/${shareId}/reject`);
            console.log('Reject response:', response);
            
            if (response.data.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Success',
                    detail: 'Share request rejected',
                    life: 3000
                });
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error rejecting share:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to reject share request',
                life: 3000
            });
        }
    };

    const handleRevokeShare = async (shareId) => {
        try {
            console.log('Revoking share:', shareId);
            const response = await axiosInstance.post(`/leads/shared/${shareId}/revoke`);
            console.log('Revoke response:', response);
            
            if (response.data.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Success',
                    detail: 'Share revoked successfully',
                    life: 3000
                });
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error revoking share:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to revoke share',
                life: 3000
            });
        }
    };

    const viewLeadDetails = (share) => {
        // Navigate to the SharedLeadDetails page
        navigate(`/app/leads/shared/${share.id}`);
    };

    const openEditAccessDialog = (share) => {
        setSelectedShareForEdit(share);
        setNewAccessLevel(share.share_type);
        setIncludeReports(share.include_reports || false);
        setEditAccessDialogVisible(true);
    };

    const handleUpdateAccess = async () => {
        if (!selectedShareForEdit || !newAccessLevel) return;

        setUpdatingAccess(true);
        try {
            const response = await axiosInstance.put(`/leads/shared/${selectedShareForEdit.id}/access`, {
                share_type: newAccessLevel,
                include_reports: includeReports
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Access level updated successfully',
                    life: 3000
                });
                setEditAccessDialogVisible(false);
                setSelectedShareForEdit(null);
                loadSharedLeads(); // Reload to reflect changes
            }
        } catch (error) {
            console.error('Error updating access level:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to update access level',
                life: 3000
            });
        } finally {
            setUpdatingAccess(false);
        }
    };

    // Unmask request handlers
    const openUnmaskDialog = (share) => {
        setSelectedShareForUnmask(share);
        setUnmaskMessage('');
        setUnmaskDialogVisible(true);
    };

    const handleRequestUnmask = async () => {
        if (!selectedShareForUnmask) return;

        setSubmittingUnmask(true);
        try {
            const response = await requestUnmask(selectedShareForUnmask.id, unmaskMessage || null);

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Request Sent',
                    detail: 'Unmask request sent to the owner',
                    life: 3000
                });
                setUnmaskDialogVisible(false);
                setSelectedShareForUnmask(null);
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error requesting unmask:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to send unmask request',
                life: 3000
            });
        } finally {
            setSubmittingUnmask(false);
        }
    };

    const handleApproveUnmask = async (shareId) => {
        try {
            const response = await approveUnmask(shareId);

            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Approved',
                    detail: 'Unmask request approved. Recipient can now see full details.',
                    life: 3000
                });
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error approving unmask:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to approve unmask request',
                life: 3000
            });
        }
    };

    const handleRejectUnmask = async (shareId) => {
        try {
            const response = await rejectUnmask(shareId);

            if (response.success) {
                toast.current?.show({
                    severity: 'info',
                    summary: 'Rejected',
                    detail: 'Unmask request rejected',
                    life: 3000
                });
                loadSharedLeads();
            }
        } catch (error) {
            console.error('Error rejecting unmask:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to reject unmask request',
                life: 3000
            });
        }
    };

    // Column templates
    const nameTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-2">
                <span className="font-bold">{rowData.lead.full_name || '-'}</span>
            </div>
        );
    };

    const emailTemplate = (rowData) => {
        return <span>{rowData.lead.email ? maskEmail(rowData.lead.email) : '-'}</span>;
    };

    const shareTypeTemplate = (rowData) => {
        const getSeverity = (type) => {
            switch (type) {
                case 'view': return 'info';
                case 'edit': return 'warning';
                case 'full': return 'success';
                default: return 'info';
            }
        };
        
        const getLabel = (type) => {
            switch (type) {
                case 'view': return 'View Only';
                case 'edit': return 'Can Edit';
                case 'full': return 'Full Access';
                default: return type;
            }
        };
        
        return <Tag severity={getSeverity(rowData.share_type)} value={getLabel(rowData.share_type)} />;
    };

    const sharedByTemplate = (rowData) => {
        return (
            <div>
                <div className="font-semibold">{rowData.shared_by.name}</div>
                <div className="text-sm text-gray-500">{rowData.shared_by.email}</div>
            </div>
        );
    };

    const sharedWithTemplate = (rowData) => {
        return (
            <div>
                <div className="font-semibold">{rowData.shared_with.name}</div>
                <div className="text-sm text-gray-500">{rowData.shared_with.email}</div>
            </div>
        );
    };

    const dateTemplate = (rowData) => {
        return new Date(rowData.shared_at).toLocaleDateString();
    };

    const expiryTemplate = (rowData) => {
        if (!rowData.expires_at) return <span className="text-gray-500">Never</span>;
        
        const expiryDate = new Date(rowData.expires_at);
        const isExpired = expiryDate < new Date();
        
        return (
            <Tag severity={isExpired ? 'danger' : 'success'} 
                 value={expiryDate.toLocaleDateString()} />
        );
    };

    const pendingActionsTemplate = (rowData) => {
        return (
            <div className="flex gap-2">
                <Button
                    icon="pi pi-check"
                    className="p-button-success p-button-sm"
                    tooltip="Accept"
                    aria-label="Accept"
                    onClick={() => handleAcceptShare(rowData.id)}
                />
                <Button
                    icon="pi pi-times"
                    className="p-button-danger p-button-sm"
                    tooltip="Reject"
                    aria-label="Reject"
                    onClick={() => handleRejectShare(rowData.id)}
                />
                <Button
                    icon="pi pi-eye"
                    className="p-button-info p-button-text p-button-sm"
                    tooltip="View Details"
                    aria-label="View Details"
                    onClick={() => viewLeadDetails(rowData)}
                />
            </div>
        );
    };

    // Masking status template for "Shared With Me" tab
    const maskingStatusTemplate = (rowData) => {
        if (!rowData.is_masked) {
            return (
                <Tag value="Unmasked" severity="success" icon="pi pi-lock-open" />
            );
        }

        const unmaskStatus = rowData.unmask_status;

        if (unmaskStatus === 'pending') {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Masked" severity="warning" icon="pi pi-lock" />
                    <span className="text-xs text-orange-500">Request pending</span>
                </div>
            );
        }

        if (unmaskStatus === 'rejected') {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Masked" severity="danger" icon="pi pi-lock" />
                    <span className="text-xs text-red-500">Request denied</span>
                </div>
            );
        }

        return (
            <div className="flex align-items-center gap-2">
                <Tag value="Masked" severity="secondary" icon="pi pi-lock" />
            </div>
        );
    };

    const sharedWithMeActionsTemplate = (rowData) => {
        const shareType = rowData.share_type;
        const canEdit = shareType === 'edit' || shareType === 'full';
        const canShare = shareType === 'full';
        const isMasked = rowData.is_masked;
        const unmaskStatus = rowData.unmask_status;
        const canRequestUnmask = isMasked && unmaskStatus !== 'pending';

        return (
            <div className="flex gap-2">
                <Button
                    label="View"
                    icon="pi pi-eye"
                    className="p-button-sm p-button-info"
                    onClick={() => viewLeadDetails(rowData)}
                    tooltip="View lead details"
                />
                {canRequestUnmask && (
                    <Button
                        icon="pi pi-lock-open"
                        className="p-button-sm p-button-help p-button-text"
                        onClick={() => openUnmaskDialog(rowData)}
                        tooltip="Request to unmask details"
                        aria-label="Request to unmask details"
                    />
                )}
                {canEdit && (
                    <Button
                        icon="pi pi-pencil"
                        className="p-button-sm p-button-warning p-button-text"
                        onClick={() => viewLeadDetails(rowData)}
                        tooltip="Edit lead"
                        aria-label="Edit lead"
                    />
                )}
                {canShare && (
                    <Button
                        icon="pi pi-share-alt"
                        className="p-button-sm p-button-success p-button-text"
                        onClick={() => viewLeadDetails(rowData)}
                        tooltip="Re-share lead"
                        aria-label="Re-share lead"
                    />
                )}
            </div>
        );
    };

    // Unmask request status template for "Shared By Me" tab
    const unmaskRequestTemplate = (rowData) => {
        const unmaskStatus = rowData.unmask_status;

        if (unmaskStatus === 'approved') {
            return <Tag value="Unmasked" severity="success" icon="pi pi-lock-open" />;
        }

        if (unmaskStatus === 'pending') {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Request Pending" severity="warning" icon="pi pi-clock" />
                    <div className="flex gap-1 mt-1">
                        <Button
                            icon="pi pi-check"
                            className="p-button-success p-button-text p-button-sm"
                            tooltip="Approve unmask"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleApproveUnmask(rowData.id)}
                            style={{ padding: '0.25rem' }}
                            aria-label="Approve unmask"
                        />
                        <Button
                            icon="pi pi-times"
                            className="p-button-danger p-button-text p-button-sm"
                            tooltip="Reject unmask"
                            tooltipOptions={{ position: 'top' }}
                            onClick={() => handleRejectUnmask(rowData.id)}
                            style={{ padding: '0.25rem' }}
                            aria-label="Reject unmask"
                        />
                    </div>
                </div>
            );
        }

        if (unmaskStatus === 'rejected') {
            return <Tag value="Rejected" severity="secondary" icon="pi pi-times" />;
        }

        // Default: masked, no request yet
        if (rowData.mask_details) {
            return <Tag value="Masked" severity="info" icon="pi pi-lock" />;
        }

        return <Tag value="Unmasked" severity="success" icon="pi pi-lock-open" />;
    };

    const sharedByMeActionsTemplate = (rowData) => {
        const isActive = rowData.status === 'pending' || rowData.status === 'accepted';

        return (
            <div className="flex gap-2">
                {isActive && (
                    <Button
                        icon="pi pi-cog"
                        className="p-button-warning p-button-text p-button-sm"
                        tooltip="Edit Access Level"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => openEditAccessDialog(rowData)}
                        aria-label="Edit Access Level"
                    />
                )}
                <Button
                    icon="pi pi-eye"
                    className="p-button-info p-button-text p-button-sm"
                    tooltip="View Details"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => viewLeadDetails(rowData)}
                    aria-label="View Details"
                />
                {isActive && (
                    <Button
                        icon="pi pi-ban"
                        className="p-button-danger p-button-text p-button-sm"
                        tooltip="Revoke Access"
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => handleRevokeShare(rowData.id)}
                        aria-label="Revoke Access"
                    />
                )}
            </div>
        );
    };

    const statusTemplate = (rowData) => {
        const getSeverity = (status) => {
            switch (status) {
                case 'pending': return 'warning';
                case 'accepted': return 'success';
                case 'rejected': return 'danger';
                case 'revoked': return 'secondary';
                default: return 'info';
            }
        };

        return <Tag severity={getSeverity(rowData.status)} value={rowData.status.toUpperCase()} />;
    };

    // Can Re-share column template - shows Yes/No based on full access
    const canReshareTemplate = (rowData) => {
        const canShare = rowData.share_type === 'full';
        return (
            <Tag
                value={canShare ? 'Yes' : 'No'}
                severity={canShare ? 'success' : 'secondary'}
                icon={canShare ? 'pi pi-check' : 'pi pi-times'}
            />
        );
    };

    // Share origin template for "Shared With Me" - shows Shared or Returned
    const sharedWithMeTypeTemplate = (rowData) => {
        if (rowData.is_return) {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Returned" severity="help" icon="pi pi-undo" />
                    <span className="text-xs text-500">Your lead</span>
                </div>
            );
        }
        return <Tag value="Shared" severity="info" icon="pi pi-inbox" />;
    };

    // Share origin template for "Shared By Me" - shows Original, Re-share, or Return
    const shareOriginTemplate = (rowData) => {
        // Check if this is a Return (sharing back to original owner)
        if (rowData.is_return) {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Return" severity="help" icon="pi pi-undo" />
                    <span className="text-xs text-500">to owner</span>
                </div>
            );
        }
        // Check if this is a Re-share (not the lead owner)
        if (rowData.is_reshare) {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value="Re-share" severity="info" icon="pi pi-replay" />
                    {rowData.original_sharer && (
                        <span className="text-xs text-500">
                            from {rowData.original_sharer.name}
                        </span>
                    )}
                </div>
            );
        }
        // Original share (lead owner sharing their lead)
        return <Tag value="Original" severity="success" icon="pi pi-home" />;
    };

    const productTypeTemplate = (rowData) => {
        // Product type mapping from CreateLead component
        const productTypeMap = {
            'new_personal_loan': 'New Personal Loan',
            'personal_loan_top_up': 'Personal Loan Top Up',
            'personal_loan_balance_transfer': 'Personal Loan Balance Transfer',
            'personal_loan_bt_top_up': 'Personal Loan BT + Top Up',
            'personal_loan_over_draft': 'Personal Loan Over Draft',
            'new_home_loan': 'New Home Loan',
            'home_loan_top_up': 'Home Loan Top Up',
            'home_loan_balance_transfer': 'Home Loan Balance Transfer',
            'home_loan_bt_top_up': 'Home Loan BT + Top Up',
            'home_loan_over_draft': 'Home Loan Over Draft',
            'home_loan_vendor_bt': 'Home Loan Vendor BT',
            'home_loan_assignment': 'Home Loan Assignment',
            // Legacy abbreviation mappings (for backward compatibility)
            'pl': 'Personal Loan',
            'hl': 'Home Loan',
            'bl': 'Business Loan',
            'lap': 'Loan Against Property'
        };

        const productType = rowData.lead.product_type;
        return productTypeMap[productType] || productType || 'Not specified';
    };

    const phoneTemplate = (rowData) => {
        return <span>{rowData.lead.phone ? maskPhone(rowData.lead.phone) : '-'}</span>;
    };

    const panTemplate = (rowData) => {
        return <span>{rowData.lead.pan ? maskPAN(rowData.lead.pan) : '-'}</span>;
    };

    const loanAmountTemplate = (rowData) => {
        const amount = rowData.lead.loan_amount_required;
        if (!amount) return <span>-</span>;

        // Format as Indian currency
        return (
            <span className="font-semibold">
                ₹{new Intl.NumberFormat('en-IN').format(amount)}
            </span>
        );
    };

    const leadStatusTemplate = (rowData) => {
        const c = getProcessingStatusConfig(rowData.lead.lead_status);
        return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} />;
    };

    // Progress/Completion column template - replicates LeadsList.js functionality
    const completionTemplate = (rowData) => {
        // Calculate document-only completion percentage (capped at 100%)
        const docsUploaded = rowData.completion?.documents_uploaded_count || 0;
        const docsRequired = rowData.completion?.documents_required_count || 0;
        const docCompletionPercentage = docsRequired > 0 ? Math.min(100, Math.round((docsUploaded / docsRequired) * 100)) : 0;

        // Determine completion status based on document completion
        let completionStatus = 'Incomplete';
        if (docCompletionPercentage === 100) {
            completionStatus = 'Complete';
        } else if (docCompletionPercentage >= 50) {
            completionStatus = 'In Progress';
        }

        // Get severity color based on document completion
        const getSeverityColor = () => {
            if (docCompletionPercentage === 100) return '#22c55e'; // Green
            if (docCompletionPercentage >= 50) return '#f59e0b';  // Orange
            return '#ef4444'; // Red
        };

        // Generate unique tooltip ID
        const tooltipId = `tooltip-shared-lead-${rowData.id}`;

        return (
            <>
                <Tooltip target={`.${tooltipId}`} position="left">
                    <div className="p-2">
                        <div className="font-bold mb-2 text-lg">
                            📊 Completion: {docCompletionPercentage}%
                        </div>
                        <div className="mb-2">
                            📄 Documents: {docsUploaded}/{docsRequired}
                            {rowData.completion?.newly_added_count > 0 && (
                                <span className="text-blue-400 ml-1">
                                    ({rowData.completion.newly_added_count} via sharing)
                                </span>
                            )}
                        </div>

                        {/* Newly Added Documents via Sharing - Detailed Section */}
                        {rowData.completion?.newly_added_documents && rowData.completion.newly_added_documents.length > 0 && (
                            <div className="mb-2 p-2 border-round bg-blue-50">
                                <div className="font-semibold mb-1 text-blue-600">🔗 Added via Sharing:</div>
                                {rowData.completion.newly_added_documents.map((doc, index) => (
                                    <div key={index} className="ml-3 text-sm text-blue-800">
                                        • {doc.document_type} <span className="text-blue-500">by {doc.uploaded_by}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {rowData.completion?.missing_fields && rowData.completion.missing_fields.length > 0 && (
                            <div className="mb-2">
                                <div className="font-semibold mb-1 text-orange-500">❗ Missing Fields:</div>
                                {rowData.completion.missing_fields.map((field, index) => (
                                    <div key={index} className="ml-3 text-sm">• {field}</div>
                                ))}
                            </div>
                        )}

                        {rowData.completion?.missing_documents && rowData.completion.missing_documents.length > 0 && (
                            <div className="mb-2">
                                <div className="font-semibold mb-1 text-blue-500">📄 Missing Documents:</div>
                                {rowData.completion.missing_documents.map((doc, index) => (
                                    <div key={index} className="ml-3 text-sm">• {doc}</div>
                                ))}
                            </div>
                        )}

                        {rowData.completion?.document_statuses && rowData.completion.document_statuses.length > 0 && (
                            <div className="mb-2">
                                <div className="font-semibold mb-1 text-green-500">📋 Uploaded Documents:</div>
                                {rowData.completion.document_statuses.map((doc, index) => (
                                    <div key={index} className="ml-3 mb-2">
                                        <div className="flex align-items-center gap-2 mb-1">
                                            <span className="text-sm">• {doc.label}</span>
                                            {doc.verification_status === 'verified' ? (
                                                <i className="pi pi-check-circle text-green-500" style={{ fontSize: '0.8rem' }} title="Verified"></i>
                                            ) : (
                                                <i className="pi pi-times-circle text-red-500" style={{ fontSize: '0.8rem' }} title="Not Verified"></i>
                                            )}
                                            {doc.is_newly_added && (
                                                <span className="text-blue-500 text-xs font-semibold">(via sharing)</span>
                                            )}
                                            {doc.pending_queries > 0 && (
                                                <span className="text-orange-600 text-xs">({doc.pending_queries} query)</span>
                                            )}
                                        </div>
                                        {/* Show uploader info */}
                                        {doc.uploaded_by && (
                                            <div className="ml-4 text-xs text-gray-500">
                                                <i className="pi pi-user mr-1" style={{ fontSize: '0.7rem' }}></i>
                                                {doc.uploaded_by} • {doc.uploaded_at}
                                            </div>
                                        )}
                                        {doc.query_list && doc.query_list.length > 0 && (
                                            <div className="ml-4 mt-1">
                                                {doc.query_list.slice(-3).map((query, qIndex) => (
                                                    <div key={qIndex} className="text-xs text-orange-700 bg-orange-50 p-2 border-round mb-1">
                                                        <i className="pi pi-question-circle mr-1"></i>
                                                        {query.comment}
                                                    </div>
                                                ))}
                                                {doc.query_list.length > 3 && (
                                                    <div className="text-xs text-500 text-center">
                                                        +{doc.query_list.length - 3} more queries
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="text-xs text-gray-600 mt-2 pt-2 border-top-1 border-gray-300">
                            Click "View" for complete details
                        </div>
                    </div>
                </Tooltip>

                <div
                    className={`flex flex-column gap-2 ${tooltipId} cursor-pointer`}
                    onClick={() => {
                        setSelectedShareForCompletion(rowData);
                        setCompletionModalVisible(true);
                    }}
                >
                    <Tag
                        value={completionStatus}
                        severity={docCompletionPercentage === 100 ? 'success' : docCompletionPercentage >= 50 ? 'warning' : 'danger'}
                    />
                    <ProgressBar
                        value={docCompletionPercentage}
                        showValue={false}
                        style={{ height: '8px' }}
                        color={getSeverityColor()}
                     aria-label="Progress" />
                    <span className="text-xs text-center">{docCompletionPercentage}%</span>
                </div>
            </>
        );
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <h1 className="m-0">Shared Leads</h1>
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText 
                    value={globalFilter} 
                    onChange={(e) => setGlobalFilter(e.target.value)} 
                    placeholder="Search leads..." 
                    className="w-full"
                />
            </IconField>
        </div>
    );

    // Dialog footers - no longer needed since we navigate to a new page
    // const leadDetailsFooter = (
    //     <div className="flex justify-content-end">
    //         <Button 
    //             label="Close" 
    //             icon="pi pi-times" 
    //             onClick={() => setDetailsVisible(false)} 
    //         />
    //     </div>
    // );

    // const reportsDialogFooter = (
    //     <div className="flex justify-content-end">
    //         <Button 
    //             label="Close" 
    //             icon="pi pi-times" 
    //             onClick={() => {
    //                 setReportsVisible(false);
    //                 setSharedReports([]);
    //             }} 
    //         />
    //     </div>
    // );

    // Show initial loading screen
    if (initialLoad && loading) {
        return (
            <div className="shared-leads-container">
                <Toast ref={toast} />
                
                {/* Loading State */}
                <div className="surface-card border-round shadow-2 p-4 mb-4">
                    <div className="flex align-items-center mb-4">
                        <i className="pi pi-share-alt text-primary text-2xl mr-3"></i>
                        <div>
                            <h4 className="m-0">Lead Sharing Management</h4>
                            <p className="text-500 text-sm m-0 mt-1">Loading shared leads data...</p>
                        </div>
                    </div>
                    
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="text-center">
                            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                            <div className="mt-3 text-500">
                                <p>Loading your shared leads...</p>
                                <p className="text-xs">This may take a moment</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="shared-leads-container">
            <Toast ref={toast} />
            
            {/* Header */}
            <div className="surface-card border-round shadow-2 p-4 mb-4">
                <div className="flex align-items-center">
                    <div className="flex align-items-center justify-content-center bg-blue-100 border-round mr-3" 
                         style={{width: '2.5rem', height: '2.5rem'}}>
                        <i className="pi pi-share-alt text-blue-500 text-xl"></i>
                    </div>
                    <div>
                        <h5 className="m-0 text-900">Lead Sharing Management</h5>
                        <p className="text-500 text-sm m-0 mt-1">
                            Manage your shared leads • {sharedWithMe.length + pendingShares.length + sharedByMe.length} total shares
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Below 768px the three tab headers overflow the width and the
                last one gets clipped, so the nav stacks vertically instead.
                Only the nav changes — the active-tab indicator moves from the
                bottom edge to the left edge by flipping which border side gets
                the width; the theme's transparent/primary border-COLOUR logic
                is left alone so the active state still tracks correctly. */}
            <style>{`
                @media screen and (max-width: 767px) {
                    .shared-leads-tabs .p-tabview-nav {
                        flex-direction: column;
                        border-width: 0;
                    }
                    .shared-leads-tabs .p-tabview-nav li {
                        width: 100%;
                    }
                    .shared-leads-tabs .p-tabview-nav li .p-tabview-nav-link {
                        width: 100%;
                        justify-content: flex-start;
                        border-width: 0 0 0 3px;
                    }
                }
            `}</style>
            <Panel className="mb-4">
                <TabView
                    activeIndex={activeIndex}
                    onTabChange={(e) => setActiveIndex(e.index)}
                    className="shared-leads-tabs"
                >
                    <TabPanel 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-inbox"></i>
                                <span>Shared With Me</span>
                                {sharedWithMe.length > 0 && <Badge value={sharedWithMe.length} />}
                            </div>
                        }
                    >
                        {sharedWithMe.length === 0 ? (
                            <Message severity="info" text="No leads have been shared with you" />
                        ) : (
                            <DataTable
                                key={`shared-with-me-table-${isMasked}`}
                                value={sharedWithMe}
                                loading={loading && !initialLoad}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                globalFilter={globalFilter}
                                header={header}
                                emptyMessage="No shared leads found"
                                className="p-datatable-sm"
                            >
                                <Column field="lead.id" header="Lead ID" sortable style={{ width: '8rem' }} />
                                <Column body={nameTemplate} header="Lead Name" sortable />
                                <Column body={phoneTemplate} header="Phone" sortable style={{ width: '10rem' }} />
                                <Column body={panTemplate} header="PAN" sortable style={{ width: '9rem' }} />
                                <Column body={productTypeTemplate} header="Product Type" sortable />
                                <Column body={loanAmountTemplate} header="Loan Amount" sortable style={{ width: '10rem' }} />
                                <Column body={leadStatusTemplate} header="Lead Status" sortable style={{ width: '9rem' }} />
                                <Column body={completionTemplate} header="Progress" style={{ width: '12rem' }} />
                                <Column body={sharedByTemplate} header="Shared By" />
                                <Column body={sharedWithMeTypeTemplate} header="Type" style={{ width: '8rem' }} />
                                <Column body={maskingStatusTemplate} header="Privacy" style={{ width: '9rem' }} />
                                <Column body={shareTypeTemplate} header="Access Level" />
                                <Column body={dateTemplate} header="Shared On" sortable style={{ width: '8rem' }} />
                                <Column body={sharedWithMeActionsTemplate} header="Actions" style={{ width: '12rem' }} />
                            </DataTable>
                        )}
                    </TabPanel>
                    
                    <TabPanel 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock"></i>
                                <span>Pending Requests</span>
                                {pendingShares.length > 0 && <Badge value={pendingShares.length} severity="warning" />}
                            </div>
                        }
                    >
                        {pendingShares.length === 0 ? (
                            <Message severity="info" text="No pending share requests" />
                        ) : (
                            <DataTable
                                key={`pending-shares-table-${isMasked}`}
                                value={pendingShares}
                                loading={loading && !initialLoad}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                globalFilter={globalFilter}
                                emptyMessage="No pending requests"
                                className="p-datatable-sm"
                            >
                                <Column field="lead.id" header="Lead ID" sortable style={{ width: '8rem' }} />
                                <Column body={nameTemplate} header="Lead Name" sortable />
                                <Column body={phoneTemplate} header="Phone" sortable style={{ width: '10rem' }} />
                                <Column body={panTemplate} header="PAN" sortable style={{ width: '9rem' }} />
                                <Column body={productTypeTemplate} header="Product Type" sortable />
                                <Column body={loanAmountTemplate} header="Loan Amount" sortable style={{ width: '10rem' }} />
                                <Column body={leadStatusTemplate} header="Lead Status" sortable style={{ width: '9rem' }} />
                                <Column body={completionTemplate} header="Progress" style={{ width: '12rem' }} />
                                <Column body={sharedByTemplate} header="Shared By" />
                                <Column body={shareTypeTemplate} header="Access Level" style={{ width: '8rem' }} />
                                <Column body={dateTemplate} header="Requested On" sortable style={{ width: '8rem' }} />
                                <Column body={pendingActionsTemplate} header="Actions" style={{ width: '10rem' }} />
                            </DataTable>
                        )}
                    </TabPanel>
                    
                    <TabPanel 
                        header={
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-share-alt"></i>
                                <span>Shared By Me</span>
                                {sharedByMe.length > 0 && <Badge value={sharedByMe.length} />}
                            </div>
                        }
                    >
                        {sharedByMe.length === 0 ? (
                            <Message severity="info" text="You haven't shared any leads" />
                        ) : (
                            <DataTable
                                key={`shared-by-me-table-${isMasked}`}
                                value={sharedByMe}
                                loading={loading && !initialLoad}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25]}
                                globalFilter={globalFilter}
                                emptyMessage="No shared leads found"
                                className="p-datatable-sm"
                            >
                                <Column field="lead.id" header="Lead ID" sortable style={{ width: '6rem' }} />
                                <Column body={shareOriginTemplate} header="Type" style={{ width: '8rem' }} />
                                <Column body={nameTemplate} header="Lead Name" sortable />
                                <Column body={phoneTemplate} header="Phone" sortable style={{ width: '10rem' }} />
                                <Column body={panTemplate} header="PAN" sortable style={{ width: '9rem' }} />
                                <Column body={productTypeTemplate} header="Product Type" sortable />
                                <Column body={loanAmountTemplate} header="Loan Amount" sortable style={{ width: '10rem' }} />
                                <Column body={leadStatusTemplate} header="Lead Status" sortable style={{ width: '9rem' }} />
                                <Column body={completionTemplate} header="Progress" style={{ width: '12rem' }} />
                                <Column body={sharedWithTemplate} header="Shared With" />
                                <Column body={shareTypeTemplate} header="Access Level" style={{ width: '8rem' }} />
                                <Column body={unmaskRequestTemplate} header="Privacy" style={{ width: '10rem' }} />
                                <Column body={statusTemplate} header="Status" style={{ width: '7rem' }} />
                                <Column body={dateTemplate} header="Shared On" sortable style={{ width: '8rem' }} />
                                <Column body={sharedByMeActionsTemplate} header="Actions" style={{ width: '10rem' }} />
                            </DataTable>
                        )}
                    </TabPanel>
                </TabView>
            </Panel>

            {/* Edit Access Level Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-cog text-primary text-xl"></i>
                        <span className="font-bold">Edit Access Level</span>
                    </div>
                }
                visible={editAccessDialogVisible}
                onHide={() => {
                    setEditAccessDialogVisible(false);
                    setSelectedShareForEdit(null);
                }}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setEditAccessDialogVisible(false);
                                setSelectedShareForEdit(null);
                            }}
                        />
                        <Button
                            label="Update Access"
                            icon="pi pi-check"
                            onClick={handleUpdateAccess}
                            loading={updatingAccess}
                            disabled={!newAccessLevel || (newAccessLevel === selectedShareForEdit?.share_type && includeReports === (selectedShareForEdit?.include_reports || false))}
                        />
                    </div>
                }
            >
                {selectedShareForEdit && (
                    <div className="p-fluid">
                        {/* Current Share Info */}
                        <div className="surface-100 border-round p-3 mb-4">
                            <div className="flex align-items-center gap-3 mb-2">
                                <i className="pi pi-user text-primary text-xl"></i>
                                <div>
                                    <div className="font-semibold text-900">{selectedShareForEdit.shared_with?.name}</div>
                                    <div className="text-sm text-600">{selectedShareForEdit.shared_with?.email}</div>
                                </div>
                            </div>
                            <div className="flex align-items-center gap-2 mt-2">
                                <span className="text-600">Lead:</span>
                                <span className="font-semibold">{selectedShareForEdit.lead?.full_name}</span>
                            </div>
                            <div className="flex align-items-center gap-2 mt-1">
                                <span className="text-600">Current Access:</span>
                                <Tag
                                    value={accessLevelOptions.find(o => o.value === selectedShareForEdit.share_type)?.label}
                                    severity={accessLevelOptions.find(o => o.value === selectedShareForEdit.share_type)?.severity}
                                />
                            </div>
                            <div className="flex align-items-center gap-2 mt-1">
                                <span className="text-600">Reports:</span>
                                <Tag
                                    value={selectedShareForEdit.include_reports ? 'Included' : 'Not Included'}
                                    severity={selectedShareForEdit.include_reports ? 'success' : 'secondary'}
                                    icon={selectedShareForEdit.include_reports ? 'pi pi-check' : 'pi pi-times'}
                                />
                            </div>
                        </div>

                        {/* Access Level Selection */}
                        <div className="field">
                            <label className="font-semibold mb-2 block">New Access Level</label>
                            <Dropdown
                                value={newAccessLevel}
                                options={accessLevelOptions}
                                onChange={(e) => setNewAccessLevel(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select access level"
                                className="w-full"
                                itemTemplate={(option) => (
                                    <div className="flex align-items-center gap-2">
                                        <i className={`pi ${option.icon}`}></i>
                                        <span>{option.label}</span>
                                    </div>
                                )}
                                valueTemplate={(option) => {
                                    if (option) {
                                        return (
                                            <div className="flex align-items-center gap-2">
                                                <i className={`pi ${option.icon}`}></i>
                                                <span>{option.label}</span>
                                            </div>
                                        );
                                    }
                                    return <span>Select access level</span>;
                                }}
                            />
                        </div>

                        {/* Include Reports Checkbox */}
                        <div className="field mt-4">
                            <div className="flex align-items-center gap-3 p-3 surface-100 border-round">
                                <Checkbox
                                    inputId="includeReports"
                                    checked={includeReports}
                                    onChange={(e) => setIncludeReports(e.checked)}
                                />
                                <label htmlFor="includeReports" className="cursor-pointer">
                                    <div className="flex align-items-center gap-2">
                                        <i className="pi pi-file text-primary"></i>
                                        <div>
                                            <div className="font-semibold">Include Fetched Reports</div>
                                            <div className="text-sm text-600">Share all previously fetched reports with this user</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Access Level Description */}
                        <div className="surface-50 border-round p-3 mt-3">
                            <div className="text-sm">
                                {newAccessLevel === 'view' && (
                                    <div className="flex align-items-start gap-2">
                                        <i className="pi pi-eye text-blue-500 mt-1"></i>
                                        <div>
                                            <div className="font-semibold text-blue-700">View Only</div>
                                            <div className="text-600">Can view lead details, documents, and reports. Cannot make any changes.</div>
                                        </div>
                                    </div>
                                )}
                                {newAccessLevel === 'edit' && (
                                    <div className="flex align-items-start gap-2">
                                        <i className="pi pi-pencil text-orange-500 mt-1"></i>
                                        <div>
                                            <div className="font-semibold text-orange-700">Can Edit</div>
                                            <div className="text-600">Can view and edit lead details, upload/delete documents. Cannot fetch new reports or re-share.</div>
                                        </div>
                                    </div>
                                )}
                                {newAccessLevel === 'full' && (
                                    <div className="flex align-items-start gap-2">
                                        <i className="pi pi-star text-green-500 mt-1"></i>
                                        <div>
                                            <div className="font-semibold text-green-700">Full Access</div>
                                            <div className="text-600">Full access including fetching new reports (uses their credits) and re-sharing with others.</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Unmask Request Dialog */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-lock-open text-primary text-xl"></i>
                        <span className="font-bold">Request to Unmask Details</span>
                    </div>
                }
                visible={unmaskDialogVisible}
                onHide={() => {
                    setUnmaskDialogVisible(false);
                    setSelectedShareForUnmask(null);
                    setUnmaskMessage('');
                }}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => {
                                setUnmaskDialogVisible(false);
                                setSelectedShareForUnmask(null);
                                setUnmaskMessage('');
                            }}
                        />
                        <Button
                            label="Send Request"
                            icon="pi pi-send"
                            onClick={handleRequestUnmask}
                            loading={submittingUnmask}
                        />
                    </div>
                }
            >
                {selectedShareForUnmask && (
                    <div className="p-fluid">
                        {/* Lead Info */}
                        <div className="surface-100 border-round p-3 mb-4">
                            <div className="flex align-items-center gap-3 mb-2">
                                <i className="pi pi-user text-primary text-xl"></i>
                                <div>
                                    <div className="font-semibold text-900">{selectedShareForUnmask.lead?.full_name}</div>
                                    <div className="text-sm text-600">Lead ID: {selectedShareForUnmask.lead?.id}</div>
                                </div>
                            </div>
                            <div className="flex align-items-center gap-2 mt-2">
                                <span className="text-600">Shared by:</span>
                                <span className="font-semibold">{selectedShareForUnmask.shared_by?.name}</span>
                            </div>
                        </div>

                        {/* Info Message */}
                        <Message
                            severity="info"
                            className="mb-3 w-full"
                            text="Some personal details like phone, email, and PAN are masked. Send a request to the owner to view full details."
                        />

                        {/* Optional Message */}
                        <div className="field">
                            <label className="font-semibold mb-2 block">
                                Message to Owner (Optional)
                            </label>
                            <InputTextarea
                                value={unmaskMessage}
                                onChange={(e) => setUnmaskMessage(e.target.value)}
                                rows={3}
                                placeholder="Explain why you need access to the full details..."
                                className="w-full"
                                maxLength={500}
                            />
                            <small className="text-500">
                                {unmaskMessage.length}/500 characters
                            </small>
                        </div>

                        {/* What will be unmasked */}
                        <div className="surface-50 border-round p-3 mt-3">
                            <div className="font-semibold mb-2">Details that will be unmasked:</div>
                            <div className="flex flex-wrap gap-2">
                                <Tag value="Phone Number" icon="pi pi-phone" severity="secondary" />
                                <Tag value="Email Address" icon="pi pi-envelope" severity="secondary" />
                                <Tag value="PAN" icon="pi pi-id-card" severity="secondary" />
                                <Tag value="Address" icon="pi pi-map-marker" severity="secondary" />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            {/* Completion Status Modal */}
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        {(() => {
                            const upCount = selectedShareForCompletion?.completion?.documents_uploaded_count || 0;
                            const reqCount = selectedShareForCompletion?.completion?.documents_required_count || 0;
                            const pct = reqCount > 0 ? Math.min(100, Math.round((upCount / reqCount) * 100)) : 0;
                            return <i className={`pi ${pct === 100 ? 'pi-check-circle text-green-500' : 'pi-exclamation-triangle text-orange-500'} text-2xl`}></i>;
                        })()}
                        <span>Document Completion Status</span>
                        {selectedShareForCompletion?.lead?.full_name && (
                            <span className="text-500 font-normal ml-2">- {selectedShareForCompletion.lead.full_name}</span>
                        )}
                    </div>
                }
                visible={completionModalVisible}
                onHide={() => {
                    setCompletionModalVisible(false);
                    setSelectedShareForCompletion(null);
                }}
                style={{ width: '700px', maxWidth: '95vw' }}
                modal
                className="completion-modal"
            >
                {selectedShareForCompletion?.completion && (() => {
                    // Calculate document-only completion percentage (capped at 100%)
                    const uploadedCount = selectedShareForCompletion.completion.documents_uploaded_count || 0;
                    const requiredCount = selectedShareForCompletion.completion.documents_required_count || 0;
                    const modalDocCompletionPct = requiredCount > 0 ? Math.min(100, Math.round((uploadedCount / requiredCount) * 100)) : 0;

                    return (
                    <div className="p-2">
                        {/* Progress Bar Section */}
                        <div className="mb-4">
                            <div className="flex justify-content-between align-items-center mb-2">
                                <span className="font-semibold text-900">Overall Completion</span>
                                <span className={`font-bold text-lg ${modalDocCompletionPct === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                                    {modalDocCompletionPct}%
                                </span>
                            </div>
                            <ProgressBar
                                value={modalDocCompletionPct}
                                showValue={false}
                                className="h-1rem"
                                style={{ backgroundColor: '#e5e7eb' }}
                                color={modalDocCompletionPct === 100 ? '#22c55e' : '#f97316'}
                             aria-label="Progress" />
                            <div className="flex justify-content-between mt-2 text-sm text-600">
                                <span>
                                    {uploadedCount} uploaded
                                    {selectedShareForCompletion.completion.newly_added_count > 0 && (
                                        <span className="text-blue-500 ml-1">
                                            ({selectedShareForCompletion.completion.newly_added_count} via sharing)
                                        </span>
                                    )}
                                </span>
                                <span>{requiredCount} total required</span>
                            </div>
                        </div>

                        {/* Added via Sharing Section */}
                        {selectedShareForCompletion.completion.newly_added_documents && selectedShareForCompletion.completion.newly_added_documents.length > 0 && (
                            <div className="mb-4">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-share-alt text-blue-500"></i>
                                    <span className="font-semibold text-900">Added via Sharing</span>
                                    <Badge value={selectedShareForCompletion.completion.newly_added_documents.length} severity="info" />
                                </div>
                                <div className="surface-100 border-round p-3 border-left-3 border-blue-500">
                                    <div className="grid">
                                        {selectedShareForCompletion.completion.newly_added_documents.map((doc, index) => (
                                            <div key={index} className="col-12 md:col-6">
                                                <div className="flex align-items-center gap-2 p-2 mb-1 surface-0 border-round border-1 border-blue-200">
                                                    <i className="pi pi-file text-blue-500"></i>
                                                    <div className="flex flex-column">
                                                        <span className="text-sm font-medium text-900">{doc.document_type}</span>
                                                        <span className="text-xs text-500">
                                                            <i className="pi pi-user mr-1" style={{ fontSize: '0.65rem' }}></i>
                                                            {doc.uploaded_by} • {doc.uploaded_at}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Missing Fields Section */}
                        {selectedShareForCompletion.completion.missing_fields && selectedShareForCompletion.completion.missing_fields.length > 0 && (
                            <div className="mb-4">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-user text-orange-500"></i>
                                    <span className="font-semibold text-900">Missing Fields</span>
                                    <Badge value={selectedShareForCompletion.completion.missing_fields.length} severity="warning" />
                                </div>
                                <div className="surface-100 border-round p-3">
                                    <div className="grid">
                                        {selectedShareForCompletion.completion.missing_fields.map((field, index) => (
                                            <div key={index} className="col-12 md:col-6">
                                                <div className="flex align-items-center gap-2 p-2 mb-1 surface-0 border-round border-1 surface-border">
                                                    <i className="pi pi-times-circle text-orange-400"></i>
                                                    <span className="text-800">{field}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Missing Documents Section */}
                        {selectedShareForCompletion.completion.missing_documents && selectedShareForCompletion.completion.missing_documents.length > 0 && (
                            <div className="mb-4">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-file text-red-500"></i>
                                    <span className="font-semibold text-900">Missing Documents</span>
                                    <Badge value={selectedShareForCompletion.completion.missing_documents.length} severity="danger" />
                                </div>
                                <div className="surface-100 border-round p-3">
                                    <div className="grid">
                                        {selectedShareForCompletion.completion.missing_documents.map((doc, index) => (
                                            <div key={index} className="col-12 md:col-6">
                                                <div className="flex align-items-center gap-2 p-2 mb-1 surface-0 border-round border-1 surface-border">
                                                    <i className="pi pi-times-circle text-red-400"></i>
                                                    <span className="text-800">{doc}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Uploaded Documents with Verification Status */}
                        {selectedShareForCompletion.completion.document_statuses && selectedShareForCompletion.completion.document_statuses.length > 0 && (
                            <div className="mb-4">
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-check-circle text-green-500"></i>
                                    <span className="font-semibold text-900">Uploaded Documents</span>
                                    <Badge value={selectedShareForCompletion.completion.documents_uploaded_count || selectedShareForCompletion.completion.document_statuses.length} severity="success" />
                                </div>
                                <div className="surface-100 border-round p-3">
                                    <div className="grid">
                                        {selectedShareForCompletion.completion.document_statuses.map((doc, index) => (
                                            <div key={index} className="col-12 md:col-6">
                                                <div className="flex align-items-center justify-content-between p-2 mb-1 surface-0 border-round border-1 surface-border">
                                                    <div className="flex align-items-center gap-2">
                                                        <i className="pi pi-file text-green-500"></i>
                                                        <span className="text-800 text-sm">{doc.label}</span>
                                                    </div>
                                                    <div className="flex align-items-center gap-1">
                                                        {doc.verification_status === 'verified' ? (
                                                            <Tag value="Verified" severity="success" className="text-xs" />
                                                        ) : doc.verification_status === 'rejected' ? (
                                                            <Tag value="Rejected" severity="danger" className="text-xs" />
                                                        ) : (
                                                            <Tag value="Pending" severity="warning" className="text-xs" />
                                                        )}
                                                        {doc.pending_queries > 0 && (
                                                            <Badge value={doc.pending_queries} severity="warning" className="text-xs" />
                                                        )}
                                                    </div>
                                                </div>
                                                {doc.query_list && doc.query_list.length > 0 && (
                                                    <div className="ml-4 mt-1 mb-2">
                                                        {doc.query_list.slice(-3).map((query, qIndex) => (
                                                            <div key={qIndex} className="text-xs text-orange-700 bg-orange-50 p-2 border-round mb-1">
                                                                <i className="pi pi-question-circle mr-1"></i>
                                                                {query.comment}
                                                            </div>
                                                        ))}
                                                        {doc.query_list.length > 3 && (
                                                            <div className="text-xs text-500 text-center">
                                                                +{doc.query_list.length - 3} more queries
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* All Complete Message */}
                        {modalDocCompletionPct === 100 && (
                            <div className="p-3 border-round bg-green-50 border-left-3 border-green-500">
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-green-600 text-xl"></i>
                                    <span className="text-green-800 font-medium">
                                        All required documents have been uploaded!
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Action Button */}
                        <div className="flex justify-content-end mt-4 pt-3 border-top-1 surface-border">
                            <Button
                                label="View Lead Details"
                                icon="pi pi-eye"
                                className="p-button-primary"
                                onClick={() => {
                                    setCompletionModalVisible(false);
                                    navigate(`/app/leads/shared/${selectedShareForCompletion.id}`);
                                }}
                            />
                        </div>
                    </div>
                    );
                })()}
            </Dialog>
        </div>
    );
};

export default SharedLeads; 
